/**
 * AG-UI client for AgentCore Runtime.
 *
 * The browser calls the runtime directly — no API Gateway or Lambda proxy.
 * AgentCore returns permissive CORS headers (`access-control-allow-origin: *`
 * with `authorization` allowed), so a plain `fetch` with a Cognito bearer token
 * works cross-origin. A proxy would add latency and break SSE streaming.
 *
 * AG-UI is a stream of typed SSE events rather than raw text, which is what lets
 * the UI show *which tool* the agent is using. For an assistant whose whole value
 * is being grounded rather than plausible, showing "reading the Assessment Policy"
 * matters as much as the answer.
 */

export type AguiEvent =
  | { type: "RUN_STARTED"; threadId: string; runId: string }
  | { type: "TEXT_MESSAGE_START"; messageId: string; role: string }
  | { type: "TEXT_MESSAGE_CONTENT"; messageId: string; delta: string }
  | { type: "TEXT_MESSAGE_END"; messageId: string }
  | { type: "TOOL_CALL_START"; toolCallId: string; toolCallName: string }
  | { type: "TOOL_CALL_ARGS"; toolCallId: string; delta: string }
  | { type: "TOOL_CALL_END"; toolCallId: string }
  | { type: "TOOL_CALL_RESULT"; toolCallId: string; content: string }
  | { type: "RUN_FINISHED"; threadId: string; runId: string }
  | { type: "RUN_ERROR"; message: string };

/** The two agents. Must match AgentName in edu_assistant/agents/base.py. */
export type AgentName = "study" | "student_success";

export const AGENT_LABELS: Record<AgentName, string> = {
  study: "Study Guide",
  student_success: "Student Success Assistant",
};

export interface AppConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  /** One Runtime ARN per agent, written by the frontend stack. */
  runtimeArns: Record<string, string>;
  runtimeQualifier: string;
  /**
   * Cognito hosted-UI base URL. When set together with `entraProvider`, the app
   * offers "Sign in with Entra" — an authorization-code + PKCE redirect to this
   * domain, which federates to Entra. Empty means native sign-in only.
   */
  cognitoDomain?: string;
  /** The Cognito identity-provider name for Entra, used as `identity_provider`. */
  entraProvider?: string;
}

/** Session ids must be at least 33 characters, per the runtime's header contract. */
export function newSessionId(): string {
  const random = crypto.randomUUID().replace(/-/g, "");
  return `edu-${random}${random}`.slice(0, 48);
}

export async function loadConfig(): Promise<AppConfig> {
  // Deployed by CDK alongside the bundle, so the same build works against any
  // stack and no stack values are baked into the JavaScript.
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      "Could not load config.json. Deploy the frontend stack with CDK first.",
    );
  }
  return (await response.json()) as AppConfig;
}

function invocationUrl(config: AppConfig, agent: AgentName): string {
  const arn = config.runtimeArns?.[agent];
  if (!arn) {
    throw new Error(
      `No runtime ARN for the ${AGENT_LABELS[agent]} agent. Redeploy the frontend stack.`,
    );
  }
  // The URL takes the bare runtime ARN; the endpoint goes in `qualifier`.
  // Passing an endpoint-qualified ARN *and* a qualifier yields a 404.
  const escaped = encodeURIComponent(arn);
  return (
    `https://bedrock-agentcore.${config.region}.amazonaws.com` +
    `/runtimes/${escaped}/invocations?qualifier=${config.runtimeQualifier}`
  );
}

/**
 * Send a prompt and yield AG-UI events as they stream in.
 *
 * Parses SSE by hand rather than using EventSource, because EventSource cannot
 * send an Authorization header or issue a POST.
 */
export async function* invokeAgent(
  config: AppConfig,
  agent: AgentName,
  accessToken: string,
  sessionId: string,
  prompt: string,
  signal?: AbortSignal,
): AsyncGenerator<AguiEvent> {
  const response = await fetch(invocationUrl(config, agent), {
    method: "POST",
    signal,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
    },
    // AG-UI's RunAgentInput shape. `prompt` is included as well because our
    // entrypoint reads it directly, which keeps the agent usable from the CLI.
    body: JSON.stringify({
      threadId: sessionId,
      runId: crypto.randomUUID(),
      messages: [{ id: crypto.randomUUID(), role: "user", content: prompt }],
      tools: [],
      context: [],
      state: {},
      forwardedProps: {},
      prompt,
    }),
  });

  if (!response.ok) {
    const detail = await response.text();
    if (response.status === 401 || response.status === 403) {
      throw new Error("Your session expired. Please sign in again.");
    }
    throw new Error(`Agent returned ${response.status}: ${detail.slice(0, 300)}`);
  }
  if (!response.body) {
    throw new Error("Agent returned an empty response stream.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line. Keep the trailing partial frame
    // in the buffer until its terminator arrives.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";

    for (const frame of frames) {
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (!payload || payload === "[DONE]") continue;

        let parsed: unknown;
        try {
          parsed = JSON.parse(payload);
        } catch {
          // A plain-text chunk rather than an AG-UI event. Surface it as content
          // so a non-AG-UI deployment still shows something useful.
          yield {
            type: "TEXT_MESSAGE_CONTENT",
            messageId: "fallback",
            delta: payload,
          };
          continue;
        }

        if (typeof parsed === "string") {
          yield {
            type: "TEXT_MESSAGE_CONTENT",
            messageId: "fallback",
            delta: parsed,
          };
        } else if (parsed && typeof parsed === "object" && "type" in parsed) {
          yield parsed as AguiEvent;
        }
      }
    }
  }
}

/** Human-readable label for a tool name, for the activity indicator. */
export function describeTool(toolName: string): string {
  const bare = toolName.split("___").pop() ?? toolName;
  const labels: Record<string, string> = {
    // Reference data
    sms_get_program: "Reading the program rules",
    sms_get_unit: "Looking up a unit",
    sms_search_units: "Searching the unit catalogue",
    // Own record
    sms_get_my_enrolment: "Checking your enrolment",
    sms_get_my_results: "Fetching your results",
    sms_check_my_progression: "Assessing your progression",
    sms_check_my_eligibility: "Checking unit eligibility",
    sms_get_my_fees: "Checking your fees",
    sms_get_my_advising_notes: "Reading your advising notes",
    // Advisor
    sms_get_student: "Looking up a student record",
    sms_get_student_results: "Reading result history",
    sms_check_student_progression: "Assessing progression risk",
    sms_list_cohort_risks: "Ranking the cohort by risk",
    sms_cohort_metrics: "Breaking the cohort down",
    sms_cohort_segment: "Listing the students in that group",
    sms_get_student_advising_notes: "Reading advising notes",
    sms_add_advising_note: "Adding an advising note",
    sms_place_enrolment_hold: "Placing an enrolment hold",
    // Shared
    docs_search: "Searching institutional documents",
    docs_get: "Reading a document",
    recall: "Recalling earlier sessions",
  };
  return labels[bare] ?? `Using ${bare.replace(/_/g, " ")}`;
}
